import {
  useBusinessEntitiesOfCompany,
  useSupportedCorporations,
} from '@commenda/api';
import { useCommendaOSList } from '@commenda/api/commenda-os-dsl';
import { useFormTemplateKey } from '@commenda/api/form-builder/form-template-keys';
import { useFormsTemplate } from '@commenda/api/form-builder/forms-templates';
import {
  Button,
  Checkbox,
  CountryInput,
  FieldWrapper,
  Icons,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetTitle,
  SingleCombobox,
  Switcher,
  SwitcherItem,
  SwitcherList,
} from '@commenda/kakashi';
import { CorporationModel } from '@commenda/utils';
import {
  CorporationType,
  Country,
  GovernanceRole,
} from '@prisma/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useEntryCompleteness } from '../../../hooks/useEntryCompleteness';
import type {
  EntryCompletenessTarget,
  LinkedFormCompletenessSource,
} from '../../../utils/entry-completeness.types';
import { LinkedFormDslHelper } from '../../../utils/linked-form-dsl-helper';
import { EntryCompletenessStatusText } from '../EntryCompletenessStatusText';
import { OwnerSubForm } from './OwnerSubForm';
import { type OwnershipVariant, type StagedOwner, VARIANT_COPY } from './types';

interface AddOwnerModalProps {
  isOpen: boolean;
  onClose: () => void;
  variant: OwnershipVariant;
  // Partnership only — when false (Limited-Partners-only structure, e.g. Indian
  // LP), the GP field is hidden and every partner is staged as a Limited Partner.
  gpApplies?: boolean;
  companyId?: number;
  // Current corporation — excluded from the "select existing" list.
  corporationId?: number;
  // Stage-only: hands the owner back to the node. Persistence happens as one
  // reconcile on form submit (T5), so abandoning the form leaves no orphan rows.
  onStaged: (
    owner: StagedOwner,
    opts?: { isGeneralPartner?: boolean; governanceRole?: GovernanceRole },
    details?: Record<string, unknown>
  ) => void;
  // When set, the sheet opens prefilled to edit an existing row. The parent
  // passes a fresh `key` per target, so useState re-seeds from these.
  initialOwner?: StagedOwner;
  initialIsGeneralPartner?: boolean;
  // Membership only — drives the Governance Role dropdown (editable when true).
  managerManaged?: boolean;
  initialGovernanceRole?: GovernanceRole;
  // Set when the node collects a new person through its own sub-form. The
  // picker then hands off instead of offering the three inline fields, so a
  // person added from the cap table is the same full record as one added in
  // the form's people step.
  onRequestNewPerson?: () => void;
  // The person sub-form a full record answers. A person picked from the list is checked
  // against it, and one it leaves incomplete is sent back to it to fill the gaps.
  personDetailsConfig?: {
    keyId: string;
    defaultValues: Record<string, unknown>;
    onRequestDetails: (directorId: number) => void;
  };
  // Selects that person on open — used to land back on someone just created.
  preselectPersonId?: string;
  // FormTemplateKey for the rest of an entity owner's file. Present ⇒ the entity
  // tab collects it here rather than sending the user back for a second pass.
  entityDetailsKeyId?: string;
  initialDetails?: Record<string, unknown>;
}

type OwnerTab = 'entity' | 'individual';

// Sentinel so the combobox trigger can show "New entity" while in add-new mode
// without the item appearing in the selectable list (it's hidden).
const NEW_ENTITY = '__new_entity__';

// Same trick for people.
const NEW_PERSON = '__new_person__';

export const AddOwnerModal = ({
  isOpen,
  onClose,
  variant,
  gpApplies = true,
  companyId,
  corporationId,
  onStaged,
  initialOwner,
  initialIsGeneralPartner,
  managerManaged,
  initialGovernanceRole,
  onRequestNewPerson,
  personDetailsConfig,
  preselectPersonId,
  entityDetailsKeyId,
  initialDetails,
}: AddOwnerModalProps) => {
  const copy = VARIANT_COPY[variant];
  const isEditing = !!initialOwner;
  const initIndividual =
    initialOwner?.kind === 'individual' ? initialOwner : undefined;
  const initEntity = initialOwner?.kind === 'entity' ? initialOwner : undefined;
  // An existing-corporation link prefills the picker; a created-inline entity prefills the new-entity fields.
  const initNewEntity =
    initEntity && initEntity.corporationId == null ? initEntity : undefined;

  const [tab, setTab] = useState<OwnerTab>(
    initialOwner ? initialOwner.kind : preselectPersonId ? 'individual' : 'entity'
  );
  const [isGeneralPartner, setIsGeneralPartner] = useState(
    !!initialIsGeneralPartner
  );
  const [governanceRole, setGovernanceRole] = useState<GovernanceRole>(
    initialGovernanceRole ?? 'MEMBER'
  );

  // Individual tab
  const [firstName, setFirstName] = useState(initIndividual?.firstName ?? '');
  const [lastName, setLastName] = useState(initIndividual?.lastName ?? '');
  const [nationality, setNationality] = useState<Country | undefined>(
    initIndividual?.nationality
  );

  // People already captured for this company. Someone added earlier in the form is
  // persisted by the time the cap table is filled, so linking here keeps one record
  // rather than a second, unrelated copy of the same human.
  const { data: keyPersonsList } = useCommendaOSList<Record<string, unknown>>(
    'keyPerson',
    companyId ? { companyId } : undefined,
    'company.keyPersons'
  );
  const [selectedPersonId, setSelectedPersonId] = useState(
    initIndividual?.directorId != null
      ? String(initIndividual.directorId)
      : (preselectPersonId ?? '')
  );
  const [addingNewPerson, setAddingNewPerson] = useState(
    !!initIndividual && initIndividual.directorId == null
  );

  const personOptions = useMemo(
    () =>
      (Array.isArray(keyPersonsList) ? keyPersonsList : []).map((person) => {
        const id = String(person.id ?? '');
        const name = `${(person.firstName as string) ?? ''} ${
          (person.lastName as string) ?? ''
        }`.trim();
        return { value: id, label: name || `Person ${id}` };
      }),
    [keyPersonsList]
  );
  const personComboOptions = useMemo(
    () => [
      ...personOptions,
      { value: NEW_PERSON, label: 'New person', hidden: true },
    ],
    [personOptions]
  );
  const selectedPerson = useMemo(
    () =>
      (Array.isArray(keyPersonsList) ? keyPersonsList : []).find(
        (person) => String(person.id ?? '') === selectedPersonId
      ),
    [keyPersonsList, selectedPersonId]
  );

  const {
    key: personDetailsKey,
    isLoading: isLoadingPersonDetailsKey,
    error: personDetailsKeyError,
  } = useFormTemplateKey(personDetailsConfig?.keyId ?? null);
  const {
    template: personDetailsTemplate,
    isLoading: isLoadingPersonDetailsTemplate,
    error: personDetailsTemplateError,
  } = useFormsTemplate(personDetailsKey?.liveFormId ?? null);
  // A failed template fetch blocks the person ("Couldn't check details") rather than letting
  // them through unchecked; a node with no live template has nothing to require.
  const personDetailsForm = useMemo(
    (): LinkedFormCompletenessSource | null =>
      personDetailsConfig
        ? {
            dsl: personDetailsTemplate?.dsl
              ? LinkedFormDslHelper.normalize(
                  personDetailsTemplate.dsl,
                  personDetailsTemplate.type
                )
              : null,
            isLoading:
              isLoadingPersonDetailsKey || isLoadingPersonDetailsTemplate,
            hasFailed: !!personDetailsKeyError || !!personDetailsTemplateError,
          }
        : null,
    [
      personDetailsConfig,
      personDetailsTemplate,
      isLoadingPersonDetailsKey,
      isLoadingPersonDetailsTemplate,
      personDetailsKeyError,
      personDetailsTemplateError,
    ]
  );
  const personCompletenessTargets = useMemo(
    (): EntryCompletenessTarget[] =>
      !addingNewPerson && selectedPersonId && companyId
        ? [
            {
              entryId: selectedPersonId,
              readContext: {
                companyId,
                ...(corporationId ? { corporationId } : {}),
                directorId: Number(selectedPersonId),
              },
              values: { ...personDetailsConfig?.defaultValues },
            },
          ]
        : [],
    [
      addingNewPerson,
      selectedPersonId,
      companyId,
      corporationId,
      personDetailsConfig?.defaultValues,
    ]
  );
  const {
    statusByEntryId: personStatusById,
    refreshSavedValues: refreshPersonDetails,
  } = useEntryCompleteness({
    linkedForm: personDetailsForm,
    targets: personCompletenessTargets,
  });
  const selectedPersonStatus = selectedPersonId
    ? personStatusById[selectedPersonId]
    : undefined;
  // The sheet stays mounted while the details drawer is open, so re-read on its return.
  useEffect(() => {
    if (isOpen) refreshPersonDetails();
  }, [isOpen, refreshPersonDetails]);

  // Entity tab
  const { data: workspaceEntities } = useBusinessEntitiesOfCompany(
    companyId ?? 0,
    { includeNonControlled: true }
  );
  const { data: supportedCorporations } = useSupportedCorporations();
  const [selectedEntityId, setSelectedEntityId] = useState(
    initEntity?.corporationId != null ? String(initEntity.corporationId) : ''
  );
  const [addingNew, setAddingNew] = useState(!!initNewEntity);
  const [newName, setNewName] = useState(initNewEntity?.companyLegalName ?? '');
  const [newCountry, setNewCountry] = useState<Country | undefined>(
    initNewEntity?.incorporationCountry ?? Country.US
  );
  const [newJurisdiction, setNewJurisdiction] = useState<
    string | undefined
  >(initNewEntity?.jurisdiction);
  const [newEntityType, setNewEntityType] = useState<
    CorporationType | undefined
  >(initNewEntity?.entityType);
  const [newEntityLegalFormId, setNewEntityLegalFormId] = useState<
    string | undefined
  >(initNewEntity?.entityLegalFormId);

  const entityOptions = useMemo(
    () =>
      (workspaceEntities?.entities ?? [])
        .filter((e) => e.id !== corporationId)
        .map((e) => ({
          value: String(e.id),
          label: e.generalCorporationInfo.legalName,
        })),
    [workspaceEntities?.entities, corporationId]
  );

  const selectedEntity = useMemo(
    () =>
      (workspaceEntities?.entities ?? []).find(
        (e) => String(e.id) === selectedEntityId
      ),
    [workspaceEntities?.entities, selectedEntityId]
  );
  const isExisting = !addingNew && !!selectedEntity;

  // Existing entity → read-only from its record; new → from local state.
  const effCountry = isExisting
    ? selectedEntity!.generalCorporationInfo.incorporationCountry
    : newCountry;
  const effJurisdiction = isExisting
    ? selectedEntity!.generalCorporationInfo.jurisdiction
    : newJurisdiction;
  const effEntityType = isExisting
    ? selectedEntity!.generalCorporationInfo.businessEntityType
    : newEntityType;
  const effEntityLegalFormId = isExisting
    ? selectedEntity!.generalCorporationInfo.entityLegalFormId
    : newEntityLegalFormId;

  const jurisdictionOptions =
    effCountry && supportedCorporations
      ? CorporationModel.getJurisdictionList(effCountry, supportedCorporations)
      : [];
  // The content catalogue, keyed by legal form. getEntityTypes reads the legacy CorporationType
  // projection, which names roughly a third of the forms and leaves 40 of 86 countries empty.
  const entityTypeOptions =
    effCountry && effJurisdiction && supportedCorporations
      ? CorporationModel.getEntityTypeOptions(
          effCountry,
          effJurisdiction,
          supportedCorporations
        )
      : [];

  const showJurisdictionField = jurisdictionOptions.length > 1;
  const showEntityTypeField = entityTypeOptions.length > 1;

  const soleJurisdiction = (country: Country): string | undefined => {
    if (!supportedCorporations) return undefined;
    const list = CorporationModel.getJurisdictionList(
      country,
      supportedCorporations
    );
    return list.length === 1 ? list[0].id : undefined;
  };
  const soleEntityForm = (country: Country, jurisdiction: string) => {
    if (!supportedCorporations) return undefined;
    const list = CorporationModel.getEntityTypeOptions(
      country,
      jurisdiction,
      supportedCorporations
    );
    return list.length === 1 ? list[0] : undefined;
  };

  const isLinkedPerson = !addingNewPerson && !!selectedPersonId;
  const individualValid = isLinkedPerson
    ? !selectedPersonStatus
    : firstName.trim().length > 0 && !!nationality;
  const entityValid =
    isExisting ||
    (addingNew &&
      newName.trim().length > 0 &&
      (!showJurisdictionField || !!effJurisdiction) &&
      (!showEntityTypeField || !!effEntityLegalFormId));
  const canSubmit = tab === 'individual' ? individualValid : entityValid;

  const reset = () => {
    setTab('entity');
    setIsGeneralPartner(false);
    setGovernanceRole('MEMBER');
    setFirstName('');
    setLastName('');
    setNationality(undefined);
    setSelectedPersonId('');
    setAddingNewPerson(false);
    setSelectedEntityId('');
    setAddingNew(false);
    setNewName('');
    setNewCountry(Country.US);
    setNewJurisdiction(undefined);
    setNewEntityType(undefined);
    setNewEntityLegalFormId(undefined);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // The entity details live in a DSL template, so the sheet's own button drives
  // the sub-form's validated submit; staging happens once it hands the data back.
  const submitDetailsRef = useRef<(() => void) | null>(null);
  // Stable, or the sub-form re-registers on every render of this sheet.
  const registerDetailsSubmit = useCallback((submit: (() => void) | null) => {
    submitDetailsRef.current = submit;
  }, []);
  const collectsDetails = tab === 'entity' && !!entityDetailsKeyId;

  const stage = (details?: Record<string, unknown>) => {
    // Partnership: GP flag only applies when the structure has a GP; a
    // Limited-Partners-only partnership stages every partner as an LP (false).
    const gp =
      variant === 'partnership'
        ? gpApplies
          ? isGeneralPartner
          : false
        : undefined;
    // M1: only carry a role when manager-managed; otherwise every member is a
    // Member. Both individual and entity members can be Managers.
    const govRole =
      variant === 'membership' && managerManaged ? governanceRole : undefined;
    const opts = { isGeneralPartner: gp, governanceRole: govRole };

    if (tab === 'individual') {
      onStaged(
        isLinkedPerson
          ? {
              kind: 'individual',
              directorId: Number(selectedPersonId),
              firstName: String(selectedPerson?.firstName ?? '').trim(),
              lastName:
                String(selectedPerson?.lastName ?? '').trim() || undefined,
              dateOfBirth: (selectedPerson?.dateOfBirth as string) ?? undefined,
              nationality:
                (selectedPerson?.nationality as Country) ??
                (nationality as Country),
            }
          : {
              kind: 'individual',
              firstName: firstName.trim(),
              lastName: lastName.trim() || undefined,
              dateOfBirth: initIndividual?.dateOfBirth,
              nationality: nationality as Country,
            },
        opts,
        details
      );
    } else if (isExisting) {
      onStaged(
        {
          kind: 'entity',
          corporationId: selectedEntity!.id,
          companyLegalName: selectedEntity!.generalCorporationInfo.legalName,
          incorporationCountry: effCountry as Country,
          jurisdiction: effJurisdiction,
          entityType: effEntityType,
          entityLegalFormId: effEntityLegalFormId ?? undefined,
        },
        opts,
        details
      );
    } else {
      onStaged(
        {
          kind: 'entity',
          companyLegalName: newName.trim(),
          incorporationCountry: effCountry as Country,
          jurisdiction: effJurisdiction,
          entityType: effEntityType,
          entityLegalFormId: effEntityLegalFormId ?? undefined,
        },
        opts,
        details
      );
    }
    handleClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canSubmit) return;
    // The sub-form enforces its own required fields, then calls back with the
    // answers; nothing is staged until it does.
    if (collectsDetails) {
      submitDetailsRef.current?.();
      return;
    }
    stage();
  };

  // Combobox: existing entities + an "Add new entity" action. In add-new mode
  // the trigger shows "New entity" via a hidden sentinel option.
  const comboOptions = addingNew
    ? [
        { value: NEW_ENTITY, label: 'New entity', hidden: true },
        ...entityOptions,
      ]
    : entityOptions;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent side="bottom" preventCloseOnOutsideClick>
        {/* Scroll the body, not the panel, so the drag handle stays reachable. */}
        <div className="flex-1 min-h-0 w-full overflow-y-auto overscroll-contain flex flex-col items-center px-10 pt-5 pb-12">
          <form
            onSubmit={handleSubmit}
            className="w-full max-w-xl flex flex-col gap-8"
          >
            <SheetTitle className="w-full text-2xl leading-8">
              {isEditing ? `Edit ${copy.noun}` : copy.modalTitle}
            </SheetTitle>

            <Switcher value={tab} onValueChange={(v) => setTab(v as OwnerTab)}>
              <SwitcherList size="md" aria-label="Owner type">
                <SwitcherItem value="entity" size="md">
                  Entity
                </SwitcherItem>
                <SwitcherItem value="individual" size="md">
                  Individual
                </SwitcherItem>
              </SwitcherList>
            </Switcher>

            <div className="w-full bg-bg-primary border border-border-secondary rounded-lg">
              <div className="flex flex-col gap-xl p-6">
                {tab === 'individual' ? (
                  <>
                    <FieldWrapper>
                      <Label required>Person</Label>
                      <SingleCombobox
                        options={personComboOptions}
                        value={addingNewPerson ? NEW_PERSON : selectedPersonId}
                        setValue={(v) => {
                          setAddingNewPerson(false);
                          setSelectedPersonId(v);
                        }}
                        placeholder="Select or add person"
                        addItemConfig={{
                          shouldAllowAdd: true,
                          onAddItem: () => {
                            if (onRequestNewPerson) {
                              onRequestNewPerson();
                              return;
                            }
                            setAddingNewPerson(true);
                            setSelectedPersonId('');
                          },
                          placeholder: 'Add new person',
                        }}
                      />
                    </FieldWrapper>

                    {isLinkedPerson &&
                      selectedPersonStatus &&
                      personDetailsConfig && (
                        <div className="flex items-center justify-between gap-md">
                          <EntryCompletenessStatusText status={selectedPersonStatus} />
                          <Button
                            variant="secondary"
                            size="sm"
                            type="button"
                            onClick={() =>
                              personDetailsConfig.onRequestDetails(
                                Number(selectedPersonId)
                              )
                            }
                          >
                            <Icons.Edit03 className="mr-xs size-3.5" />
                            Fill
                          </Button>
                        </div>
                      )}

                    {addingNewPerson && (
                      <>
                    <div className="grid grid-cols-2 gap-4">
                      <FieldWrapper>
                        <Label required htmlFor="ownerFirstName">
                          First name
                        </Label>
                        <Input
                          id="ownerFirstName"
                          placeholder="e.g., Jane"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                        />
                      </FieldWrapper>
                      <FieldWrapper>
                        <Label htmlFor="ownerLastName">Last name</Label>
                        <Input
                          id="ownerLastName"
                          placeholder="e.g., Doe"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                        />
                      </FieldWrapper>
                    </div>

                    <FieldWrapper>
                      <Label required>Nationality</Label>
                      <CountryInput
                        type="single"
                        value={nationality ?? null}
                        onValueChange={(v) =>
                          setNationality(v ? (v as Country) : undefined)
                        }
                        placeholder="Select country"
                      />
                    </FieldWrapper>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <FieldWrapper>
                      <Label required>Entity</Label>
                      <SingleCombobox
                        options={comboOptions}
                        value={addingNew ? NEW_ENTITY : selectedEntityId}
                        setValue={(v) => {
                          setAddingNew(false);
                          setSelectedEntityId(v);
                        }}
                        placeholder="Select or add entity"
                        addItemConfig={{
                          shouldAllowAdd: true,
                          onAddItem: () => {
                            setAddingNew(true);
                            setSelectedEntityId('');
                          },
                          placeholder: 'Add new entity',
                        }}
                      />
                    </FieldWrapper>

                    {addingNew && (
                      <FieldWrapper>
                        <Label required>Entity name</Label>
                        <Input
                          id="newEntityName"
                          placeholder="e.g., Commenda Technologies Inc."
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                        />
                      </FieldWrapper>
                    )}

                    <FieldWrapper>
                      <Label required>Country</Label>
                      <CountryInput
                        type="single"
                        value={effCountry ?? null}
                        onValueChange={(v) => {
                          const country = v ? (v as Country) : undefined;
                          setNewCountry(country);
                          const j = country
                            ? soleJurisdiction(country)
                            : undefined;
                          setNewJurisdiction(j);
                          const form = country && j ? soleEntityForm(country, j) : undefined;
                          setNewEntityLegalFormId(form?.entityLegalFormId);
                          setNewEntityType(form?.corporationType ?? undefined);
                        }}
                        placeholder="Select country"
                        disabled={!addingNew}
                      />
                    </FieldWrapper>

                    {showJurisdictionField && (
                      <FieldWrapper>
                        <Label required>Jurisdiction</Label>
                        <Select
                          value={effJurisdiction ?? undefined}
                          onValueChange={(v) => {
                            const j = v;
                            setNewJurisdiction(j);
                            const form = effCountry ? soleEntityForm(effCountry, j) : undefined;
                            setNewEntityLegalFormId(form?.entityLegalFormId);
                            setNewEntityType(form?.corporationType ?? undefined);
                          }}
                          disabled={!addingNew}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select jurisdiction" />
                          </SelectTrigger>
                          <SelectContent>
                            {jurisdictionOptions.map((j) => (
                              <SelectItem key={j.id} value={j.id}>
                                {j.displayName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FieldWrapper>
                    )}

                    {showEntityTypeField && (
                      <FieldWrapper>
                        <Label required>Entity type</Label>
                        <Select
                          key={effJurisdiction ?? 'no-jurisdiction'}
                          value={effEntityLegalFormId ?? undefined}
                          onValueChange={(v) => {
                            setNewEntityLegalFormId(v);
                            setNewEntityType(
                              entityTypeOptions.find(
                                (form) => form.entityLegalFormId === v
                              )?.corporationType ?? undefined
                            );
                          }}
                          disabled={!addingNew}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select entity type" />
                          </SelectTrigger>
                          <SelectContent>
                            {entityTypeOptions.map((form) => (
                              <SelectItem
                                key={form.entityLegalFormId}
                                value={form.entityLegalFormId}
                              >
                                {form.displayName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FieldWrapper>
                    )}
                  </>
                )}

                {variant === 'membership' && (
                  <FieldWrapper>
                    <Label>Governance Role</Label>
                    <Select
                      value={managerManaged ? governanceRole : 'MEMBER'}
                      onValueChange={(v) =>
                        setGovernanceRole(v as GovernanceRole)
                      }
                      disabled={!managerManaged}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Member" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MANAGER">Manager</SelectItem>
                        <SelectItem value="MEMBER">Member</SelectItem>
                      </SelectContent>
                    </Select>
                    {!managerManaged ? (
                      <span className="txt-extra-small-regular text-text-tertiary">
                        Enable "Manager-managed" to assign Managers.
                      </span>
                    ) : null}
                  </FieldWrapper>
                )}

                {variant === 'partnership' && gpApplies && (
                  <FieldWrapper>
                    <div className="flex items-start gap-2">
                      <Checkbox
                        id="ownerIsGeneralPartner"
                        checked={isGeneralPartner}
                        onCheckedChange={(c) => setIsGeneralPartner(!!c)}
                      />
                      <div className="flex flex-col">
                        <Label htmlFor="ownerIsGeneralPartner">
                          Designate as General Partner (GP)
                        </Label>
                        <span className="txt-extra-small-regular text-text-tertiary">
                          Only one General Partner is allowed. Checking this
                          replaces the current GP if one is already designated.
                        </span>
                      </div>
                    </div>
                  </FieldWrapper>
                )}
              </div>
            </div>

            {collectsDetails ? (
              <OwnerSubForm
                keyId={entityDetailsKeyId}
                defaultValues={initialDetails}
                inline
                registerSubmit={registerDetailsSubmit}
                onSubmit={stage}
                missingTemplateHint="Link a form template in the builder to define what else to collect for an entity owner."
              />
            ) : null}

            <div className="w-full flex items-center justify-end gap-4 py-4">
              <Button
                variant="secondary"
                size="md"
                type="button"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                type="submit"
                disabled={!canSubmit}
              >
                {isEditing ? 'Save' : copy.primaryButton}
              </Button>
            </div>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
};
